import { sanitizarTexto } from "@encuentrayayuda/shared/lib/sanitizar";

const GEOCODIFICADOR_URL = import.meta.env.VITE_REVERSE_GEOCODING_URL ?? "https://nominatim.openstreetmap.org/reverse";
const cacheDirecciones = new Map();

/// OSM mapea el area urbana como "Perimetro Urbano X". El acento aparece o no
/// segun el registro, asi que se contemplan las dos formas.
function limpiarNombreCiudad(valor) {
  if (!valor) return null;
  return String(valor).replace(/^Per[íi]metro\s+Urbano\s+/i, "").trim() || null;
}

/// Areas metropolitanas: agrupan varios municipios y NO sirven como ciudad.
/// Nominatim las devuelve en la clave `city`, que es donde uno esperaria el
/// municipio. Verificado en Pereira: city = "AMCO, Area Metropolitana Centro
/// Occidente" mientras el municipio real esta en `municipality`.
const ES_AREA_METROPOLITANA = /\b(area|área)\s+metropolitana\b|^amco\b/i;

/**
 * Municipio del punto.
 *
 * `municipality` va PRIMERO, antes que `city`: es la clave que contiene el
 * municipio de verdad cuando ambas existen. Con `city` primero, todo Pereira
 * quedaba guardado como "AMCO, Area Metropolitana Centro Occidente".
 *
 * Comportamiento verificado contra el servicio real:
 *   Pereira   municipality "Pereira"  | city "AMCO, Area Metropolitana…"
 *   Medellín  sin municipality        | city "Perímetro Urbano Medellín"
 *   Armenia   sin municipality        | city "Perímetro Urbano Armenia" | county "Armenia"
 */
function extraerCiudad(address = {}) {
  const candidatos = [
    address.municipality,
    address.city,
    address.town,
    address.village,
    address.county,
  ];
  for (const candidato of candidatos) {
    const limpio = limpiarNombreCiudad(candidato);
    if (limpio && !ES_AREA_METROPOLITANA.test(limpio)) return limpio;
  }
  return null;
}

export async function buscarDireccion(latitud, longitud) {
  const clave = `${latitud.toFixed(5)},${longitud.toFixed(5)}`;
  if (cacheDirecciones.has(clave)) return cacheDirecciones.get(clave);

  const parametros = new URLSearchParams({
    lat: String(latitud),
    lon: String(longitud),
    format: "jsonv2",
    zoom: "18",
    addressdetails: "1",
    "accept-language": "es",
  });
  const respuesta = await fetch(`${GEOCODIFICADOR_URL}?${parametros}`);
  if (!respuesta.ok) throw new Error("No fue posible consultar la dirección");

  const resultado = await respuesta.json();
  const ubicacion = {
    direccion: sanitizarTexto(resultado.display_name ?? "").slice(0, 150),
    ciudad: sanitizarTexto(extraerCiudad(resultado.address) ?? "").slice(0, 80),
    departamento: sanitizarTexto(resultado.address?.state ?? "").slice(0, 60),
  };
  if (ubicacion.direccion) cacheDirecciones.set(clave, ubicacion);
  return ubicacion;
}

export function obtenerPosicionActual() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Este navegador no permite obtener la ubicación."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitud: coords.latitude, longitud: coords.longitude, precision: coords.accuracy }),
      reject,
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 5 * 60_000 },
    );
  });
}

const BUSQUEDA_URL =
  import.meta.env.VITE_FORWARD_GEOCODING_URL ?? "https://nominatim.openstreetmap.org/search";

/**
 * Geocodificacion DIRECTA: de texto a coordenadas.
 *
 * Existe para resolver una incoherencia grave: si alguien escribe "Manizales"
 * pero deja el marcador en Pereira, el reporte se guardaba y se filtraba como
 * Pereira, y quedaba invisible para quien buscara Manizales. Con esto el texto
 * escrito puede mover el pin, en vez de quedar en contradiccion silenciosa.
 *
 * Acotado a Colombia (countrycodes=co) para que "Cali" no caiga en otro pais.
 * Devuelve null si no encuentra nada — quien llama decide que hacer.
 */
export async function buscarPorTexto(direccion) {
  const texto = sanitizarTexto(direccion);
  if (texto.length < 4) return null;

  const parametros = new URLSearchParams({
    q: texto,
    format: "jsonv2",
    addressdetails: "1",
    limit: "1",
    countrycodes: "co",
    "accept-language": "es",
  });

  const respuesta = await fetch(`${BUSQUEDA_URL}?${parametros}`);
  if (!respuesta.ok) throw new Error("No fue posible buscar la direccion");

  const [resultado] = await respuesta.json();
  if (!resultado) return null;

  return {
    latitud: Number(resultado.lat),
    longitud: Number(resultado.lon),
    direccion: resultado.display_name ?? texto,
    ciudad: extraerCiudad(resultado.address),
    departamento: resultado.address?.state ?? null,
  };
}
